/* eslint-disable camelcase */
/**
 * @file EventReportEditView.jsx
 * @author Daniel Aguirre
 * @description Event Report Types View Page
 */

// Dependencies
import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Select, DatePicker, Input, Form } from 'antd';
import moment from 'moment';

// Helpers
import { formatModalDate, getFormItems } from 'pages/PatientEvents/helper';
import { setStorageItem } from '@nephro/commons/helpers/storage-helper';

// Components
import FormGenerator from 'components/FormGenerator';
import ConfirmModal from 'components/ConfirmModal';
import ButtonOutlined from 'components/ButtonOutlined';
import ButtonFilled from 'components/ButtonFilled';
import { disabledFutureDates } from 'commons/helpers/utils';

function EventReportEditView({ patientEventActions, patientActions, actions }) {
  const { getPatientEventById, getEventTypesArray } = patientEventActions;
  const { updatePatient } = patientActions;
  const { setLoading } = actions;

  const formatDate = (date) => moment(date).format('YYYY-MM-DD');
  const formatTime = (date) => moment(date || moment()).format('HH:mm:ss');

  const history = useHistory();
  const [form] = Form.useForm();
  const { id: eventTypeId } = useParams();
  const [visibleConfirm, setVisibleConfirm] = useState(false);
  const [patientEvent, setPatientEvent] = useState(null);
  const [updatedEvent, setUpdatedEvent] = useState(null);
  const [newPatientData, setNewPatientData] = useState(null);
  const [finalDataEvent, setFinalDataEvent] = useState(null);
  const [eventTypes, setEventTypes] = useState([]);
  const [formItems, setFormItems] = useState([]);

  const { t: translate } = useTranslation();

  const onGoBack = () => {
    setVisibleConfirm(false);
    history.go(-2);
  };

  const onKeepOnlyNumbersAndChars = (string) =>
    string.replace(/[^0-9-,--]/g, '');

  const getFormWithEventTypeSelect = (data, result) => {
    const eventTypeSelect = {
      item: (
        <Select>
          {data.map((eventType) => (
            <Select.Option key={eventType.title}>
              {translate(`patientEvents.${eventType.title}`)}
            </Select.Option>
          ))}
        </Select>
      ),
      name: ['patientEventType'],
      label: translate('patientEvents.eventType'),
      rules: [
        {
          required: true
        }
      ]
    };
    const items = [eventTypeSelect, ...getFormItems(translate)];
    if (result?.patientEventType === 'Hospitalización') {
      items.forEach((itm, idx) => {
        if (itm.label === translate('patientEvents.date')) items.splice(idx, 1);
      });
    }

    return items;
  };

  const setEventData = (_, resp) => {
    const patientEventResp = resp.data;
    setPatientEvent(patientEventResp);
    const data = {
      phone_number: patientEventResp.phone_number,
      description: patientEventResp.description,
      patientEventType: patientEventResp.event_type.title,
      registerDate: moment(patientEventResp.register_date),
      end_date: moment(
        patientEventResp.end_date || patientEventResp.register_date
      )
    };
    const dataArray = { paged: false, limit: 10, page: 0 };
    setUpdatedEvent(
      data,
      getEventTypesArray(dataArray, (__, resp2) => {
        setEventTypes(resp2.data);
        setFormItems(getFormWithEventTypeSelect(resp2.data, data));
      })
    );
    setLoading(false);
  };

  useEffect(() => {
    if (!updatedEvent && formItems.length === 0) {
      getPatientEventById({ eventTypeId }, setEventData);
    }
  }, [eventTypeId, updatedEvent]);

  const handleFinish = ({
    patientEventType,
    description,
    registerDate,
    end_date,
    phone_number
  }) => {
    const data = {
      id_patient_event: patientEvent.id_patient_event,
      description,
      phone_number,
      event_type: eventTypes.find(({ title }) => title === patientEventType),
      patient: patientEvent.patient,
      register_date: `${formatDate(registerDate)}T${formatTime(registerDate)}`,
      end_date: `${formatDate(end_date)}T${formatTime(end_date)}`
    };

    if (updatedEvent?.patientEventType === 'Cambio de Telefono de Contacto') {
      const dataPatient = {
        id: patientEvent.patient.id_patient,
        ...patientEvent.patient,
        contact_phone_number: phone_number
      };
      setNewPatientData(dataPatient);
    }

    setFinalDataEvent(data, setVisibleConfirm(true));
  };

  // Get and set patient info after login (only patient users)
  const onGetPatientInfo = async (type, response) => {
    if (type === 'success') {
      await setStorageItem('patientInfo', response.data);
    }
  };

  const handleEdit = () => {
    if (updatedEvent?.patientEventType === 'Cambio de Telefono de Contacto')
      updatePatient(newPatientData, onGetPatientInfo);
    const { updatePatientEvent } = patientEventActions;
    updatePatientEvent(finalDataEvent, onGoBack);
  };

  // Validate end date
  const validateEndDate = (rule, value, callback) => {
    const { registerDate, end_date } = form.getFieldsValue();
    if (moment(end_date) < moment(registerDate))
      return callback('La fecha final no puede ser menor a la fecha inicial');
    return Promise.resolve();
  };

  const aditionalFormItem = () => {
    let objectItem = [];
    if (updatedEvent?.patientEventType === 'Hospitalización') {
      objectItem = [
        {
          item: (
            <DatePicker
              disabledDate={disabledFutureDates}
              format={'DD/MM/YYYY'}
            />
          ),
          name: ['registerDate'],
          label: translate('Fecha de inicio'),
          rules: [{ required: true }]
        },
        {
          item: (
            <DatePicker
              disabledDate={disabledFutureDates}
              format={'DD/MM/YYYY'}
            />
          ),
          name: ['end_date'],
          label: translate('Fecha de fin'),
          rules: [
            { required: true },
            {
              validator: validateEndDate
            }
          ]
        }
      ];
    }

    if (updatedEvent?.patientEventType === 'Cambio de Telefono de Contacto') {
      objectItem = [
        {
          item: <Input maxLength={100} />,
          name: ['phone_number'],
          label: translate('Teléfono(s) de contacto'),
          normalize: onKeepOnlyNumbersAndChars,
          rules: [{ required: true }],
          tooltip:
            'Puedes agregar varios números y separarlos por coma(,) y (-)',
          breakpoints: { xs: 24, sm: 12, md: 12, lg: 8 }
        }
      ];
    }
    return objectItem;
  };

  return (
    <div className="p-5 text-baxterGray">
      <h1 className="mb-5" style={{ fontSize: 20 }}>
        {translate('patientEvents.edit')}
      </h1>
      <section>
        <p className="mb-5" style={{ fontSize: 18 }}>
          {translate('patientEvents.formDescription')}
        </p>
        {updatedEvent && formItems.length && (
          <FormGenerator
            formParam={form}
            initialValues={updatedEvent}
            onFinish={handleFinish}
            items={[...aditionalFormItem(), ...formItems]}
            bottomOptions={[
              <div className="w-full pr-2" key="1">
                <ButtonOutlined
                  type="cyan"
                  textButton={translate('commons.cancel')}
                  onActionClick={onGoBack}
                />
              </div>,
              <div className="w-full pl-2" key="2">
                <ButtonFilled
                  className="mt-4"
                  type={'cyan'}
                  htmlType="submit"
                  textButton={translate('commons.save')}
                />
              </div>
            ]}
          />
        )}
      </section>
      <ConfirmModal
        visibleConfirm={visibleConfirm}
        onCancel={() => setVisibleConfirm(false)}
        mode={'confirm'}
        onSubmit={handleEdit}
        onSubmitText={'patientRecords.confirm'}
        contentComponent={
          <div className="bg-highLight flex flex-col justify-center items-center p-2 rounded-md mt-2">
            <h3 className="text-base text-secondary mb-0">
              {finalDataEvent?.event_type?.title}
            </h3>
            <h3 className="text-base text-secondary mb-0 mt-2">
              <ul>
                <li>
                  {finalDataEvent?.event_type?.title === 'Hospitalización'
                    ? `${translate('Fecha inicio')}`
                    : `${translate('patientEvents.date')}`}
                  {`: ${formatModalDate(
                    finalDataEvent?.register_date,
                    translate
                  )}`}
                </li>
                {finalDataEvent?.event_type?.title === 'Hospitalización' && (
                  <li>
                    {`${translate('Fecha fin')}:
                  ${formatModalDate(finalDataEvent?.end_date, translate)}`}
                  </li>
                )}
              </ul>
            </h3>
          </div>
        }
      />
    </div>
  );
}

/**
 * Component definition of proptypes.
 */
EventReportEditView.propTypes = {
  patientEventActions: PropTypes.shape({
    updatePatientEvent: PropTypes.func.isRequired,
    getPatientEventById: PropTypes.func.isRequired,
    getEventTypesArray: PropTypes.func.isRequired
  }).isRequired,
  patientActions: PropTypes.shape({
    updatePatient: PropTypes.func.isRequired
  }).isRequired,
  actions: PropTypes.shape({
    setLoading: PropTypes.func.isRequired
  }).isRequired
};
export default EventReportEditView;
